'use client';

import React, { useState, useEffect } from 'react';
import { fetchZeroElasticityAnalysis } from '../../apiHelper';
import { Target, TrendingUp, Info, DollarSign, ArrowUpRight } from 'lucide-react';

const formatCurrency = (val) => {
    if (!val) return '₦0';
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `₦${(val / 1000).toFixed(1)}K`;
    return `₦${val.toLocaleString()}`;
};

const InvisibleProfitDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const result = await fetchZeroElasticityAnalysis({
                    min_sales: 15,
                    price_threshold: 0.05
                });
                setData(result);
            } catch (err) {
                console.error("Error fetching elasticity data:", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    if (loading) return <div className="animate-pulse h-96 bg-gray-50 rounded-3xl" />;

    const items = data?.zero_elasticity_items || [];
    const summary = data?.summary || {};

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-1">
                    <Target className="text-purple-600" size={20} />
                    <h3 className="text-xl font-bold text-gray-900">Invisible Profit</h3>
                </div>
                <p className="text-xs text-gray-500">Zero-elasticity SKU opportunities</p>
            </div>

            {/* Total Opportunity KPI */}
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-6 text-white mb-6 shadow-lg shadow-purple-200">
                <p className="text-purple-100 text-[10px] font-bold uppercase tracking-widest">30D Profit Potential</p>
                <div className="flex items-baseline gap-2 mt-2">
                    <h4 className="text-3xl font-black">{formatCurrency(data?.total_invisible_profit_30d || summary.total_invisible_profit_30d)}</h4>
                    <TrendingUp size={20} className="text-purple-200" />
                </div>
                <p className="text-[10px] text-purple-100 mt-4 leading-relaxed opacity-90">
                    Calculated from {summary.total_zero_elasticity_skus} items showing resilient demand.
                </p>
            </div>

            {/* List of Items */}
            <div className="space-y-3 overflow-y-auto max-h-[380px] pr-1 custom-scrollbar">
                {items.map((item, i) => (
                    <div key={i} className="p-4 border border-gray-100 rounded-2xl bg-white hover:bg-gray-50 transition-colors group">
                        <div className="flex justify-between items-start mb-2">
                            <div className="max-w-[70%]">
                                <p className="text-sm font-bold text-gray-800 truncate">{item.product_name}</p>
                                <p className="text-[10px] text-gray-400 font-mono">{item.sku}</p>
                            </div>
                            <div className="bg-green-50 text-green-700 text-[10px] font-bold px-2 py-1 rounded-lg">
                                +{item.price_increase_percent}%
                            </div>
                        </div>

                        <div className="flex justify-between items-end mt-4">
                            <div>
                                <p className="text-[10px] text-gray-400 uppercase font-bold">Extra Profit</p>
                                <p className="text-sm font-bold text-gray-900">{formatCurrency(item.invisible_profit_potential_30d)}</p>
                            </div>
                            <button className="p-2 bg-gray-100 text-gray-400 rounded-xl group-hover:bg-purple-600 group-hover:text-white transition-all">
                                <ArrowUpRight size={16} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Strategy Insight */}
            <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-2xl">
                    <Info className="text-purple-600 shrink-0" size={18} />
                    <p className="text-[11px] text-purple-900 leading-tight">
                        <b>Insight:</b> These items show an elasticity score of <b>{summary.avg_elasticity?.toFixed(2)}</b>. Price increases did not reduce sales volume.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default InvisibleProfitDashboard;